import { Box, Modal, Typography, useTheme } from '@mui/material';
import { Fragment, useEffect, useState } from 'react';
import Button from '@mui/material/Button/Button';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { CustomDataGrid } from 'components';
import { exportAuditLogsToExcel, viewUserLogData } from 'service';
import { colorTheme } from 'theme';
import AuditDetails, { type Row, type AuditInfo } from './AuditDetails';
import { useSelector } from 'react-redux';

type GridRowData = Record<string, unknown>;

interface UserLogs {
  userAudit: AuditInfo[];
}

const dateFormatter = (date: string): string =>
  new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }) +
  ' ' +
  new Date(date).toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const UserAudit = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [customerData, setCustomerData] = useState<AuditInfo[]>([]);
  const [openAuditModal, setOpenAuditModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<AuditInfo>();
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector((state) => state?.pagination?.customerAudit);
  // ExportExcel
  const downloadExcelFile = async (): Promise<void> => {
    const url = await exportAuditLogsToExcel('customer');
    const link = document.createElement('a');
    link.href = url as string;
    link.download = 'Customer_Audit.xlsx';
    link.click();
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'action',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Action
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.actionType.toUpperCase()}
        </Typography>
      ),
    },
    {
      field: 'actionDescription',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Action Description
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.actionDescription.toUpperCase()}
        </Typography>
      ),
    },
    {
      field: 'user',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          User Name
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.actioner.firstName} {row.actioner.lastName}
        </Typography>
      ),
    },
    {
      field: 'email',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Email address
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.actioner.email}
        </Typography>
      ),
    },
    {
      field: 'timeAndDate',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Time
        </Typography>
      ),
      flex: 1,
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormatter(row.createdAt)}
        </Typography>
      ),
    },
  ];

  const viewUserLogs = async (): Promise<void> => {
    try {
      const res = await viewUserLogData(
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
      );
      const userLogs = res as UserLogs;
      setCustomerData(userLogs?.userAudit?.rows);
      setPageCount(userLogs?.userAudit.count);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    void viewUserLogs();
  }, [pageInfo?.page, pageInfo?.pageSize]);

  return (
    <Fragment>
      <CustomDataGrid
        columns={columns}
        rows={customerData as unknown as GridRowData[]}
        onRowClick={(row: { row: AuditInfo }) => {
          setSelectedRow(row.row);
          setOpenAuditModal(true);
        }}
        getRowId={(row: GridRowData) => `${String(row.logId)}`}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        currentPage="customerAudit"
        pageCount={pageCount}
      />
      <Box display={'flex'} justifyContent={'flex-end'} mt={2}>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <Button variant="outlined" onClick={downloadExcelFile}>
          Export Data To Excel
        </Button>
      </Box>

      <Modal
        open={openAuditModal}
        onClose={() => {
          setOpenAuditModal(false);
        }}
      >
        <AuditDetails row={selectedRow as unknown as Row} />
      </Modal>
    </Fragment>
  );
};

export default UserAudit;
